/**
 * plugins/query.js
 *
 * TanStack Vue Query - the server-state layer for all Parse.Cloud.run calls
 * going forward. Each impeccable view-rebuild will move its cloud-function
 * calls onto useQuery / useMutation. Pinia keeps UI state only.
 *
 * Cache is persisted to localStorage so a hard refresh paints the dashboard
 * instantly from the last-seen data while every visible query revalidates
 * in the background. Felt latency on /, /patients, /activity drops from
 * ~1.5s (cold network) to ~50ms (cache hydrate).
 */
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { version } from '../../package.json';

// Exported so the auth boundary can wipe it. Query keys are session-scoped
// on the server but identical across accounts on the client (['profile'],
// ['triage','patientsByDoctor'], ...), so a cache that outlives a logout
// serves the previous doctor's patients to the next one.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Show cached data immediately, revalidate quietly in background.
      staleTime: 30_000,
      // 24h retention so refreshing tomorrow still hydrates instantly.
      gcTime: 24 * 60 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: 'esteps-doctor-query-cache',
    // Throttle writes - every mutation would otherwise hit localStorage.
    throttleTime: 1_000,
  });

  persistQueryClient({
    queryClient,
    persister,
    // Drop everything older than gcTime.
    maxAge: 24 * 60 * 60_000,
    // Bust the persisted cache on a new deploy. Stale schemas from the
    // old bundle would otherwise hydrate into the new app and read as
    // "wrong" data until each query refetches.
    buster: version,
  });
}

export default {
  install(app) {
    app.use(VueQueryPlugin, { queryClient });
  },
};
